import {
  navigatorLock,
  NavigatorLockAcquireTimeoutError,
} from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';

import { Database } from '../database.types';
import { getSupabaseClientKeys } from '../get-supabase-client-keys';
import { withDbFailureReporting } from '../lib/db-failure-reporter';

/**
 * BAL-1144: browser calls report database failures through the same wrapper
 * as the server clients. The global `fetch` is read per call, not captured
 * once, so an instrumentation that patches it later (Sentry) still applies.
 */
function reportingFetchOptions() {
  return {
    fetch: withDbFailureReporting(
      (input, init) => globalThis.fetch(input, init),
      'browser',
    ),
  };
}

/**
 * Module-level singleton for the browser Supabase client.
 *
 * A browser tab must run exactly ONE GoTrueClient (and therefore one
 * auto-refresh timer). Multiple instances each start their own refresh
 * ticker; when several fire in the same second near token expiry they
 * stampede Supabase Auth's `/token` endpoint, tripping `429
 * over_request_rate_limit` and `Invalid Refresh Token: Already Used`
 * (refresh tokens rotate, so the loser of the race replays a spent
 * token). Prod audit logs showed 8-20 `token_refreshed`/second and dropped
 * sessions from exactly this pattern (BAL-752).
 *
 * `@supabase/ssr@0.12` already caches a browser singleton internally, but
 * that cache is a private module var on a third-party package (with a
 * `TODO(v3)`-adjacent lifecycle) and only holds if that package resolves to
 * a single module instance in the bundle. We own the guarantee here instead
 * of leasing it: one instance per tab, keyed to `@kit/supabase`, which has a
 * single resolution across the monorepo.
 */
let browserClient: ReturnType<typeof createBrowserClient<Database>> | undefined;

/**
 * Chromium refuses to invoke a WebIDL callback whose ExecutionContext is
 * destroyed, frozen (bfcache / tab discard) or paused, throwing
 * `Error: The provided callback is no longer runnable.` and REJECTING the
 * promise returned by `navigator.locks.request` (Blink:
 * bindings/scripts/bind_gen/callback_function.py -> ThrowError; caught by
 * modules/locks/lock_manager.cc `v8::TryCatch` -> `resolver_->Reject`).
 *
 * auth-js only models a custom lock failing by TIMEOUT: `_autoRefreshTokenTick`
 * swallows `LockAcquireTimeoutError` and re-throws everything else — into the
 * floating promise from `setInterval(() => this._autoRefreshTokenTick())`.
 * That surfaces as an unhandled rejection with zero app frames (BAL-792 /
 * BALLEE-GH). The lockless default path never re-throws; our `lock:` opt-in
 * (BAL-752) is what installs the escape route, so the guard belongs here.
 *
 * "Page is frozen, cannot refresh now" IS a lock-acquisition failure, so we
 * map it onto the failure mode auth-js already handles gracefully: skip this
 * tick, retry on the next one (30s). Matched narrowly on the exact Blink
 * message so a genuine lock error still propagates unchanged.
 */
const CALLBACK_NOT_RUNNABLE = /the provided callback is no longer runnable/i;

async function lifecycleSafeNavigatorLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> {
  try {
    return await navigatorLock(name, acquireTimeout, fn);
  } catch (error) {
    if (error instanceof Error && CALLBACK_NOT_RUNNABLE.test(error.message)) {
      throw new NavigatorLockAcquireTimeoutError(
        `Acquiring an exclusive Navigator LockManager lock "${name}" was aborted: page lifecycle state changed (frozen or destroyed)`,
      );
    }

    throw error;
  }
}

/**
 * The Web Locks API is not universally available: it requires a secure context
 * and is absent in some iOS WKWebView / in-app-browser embeddings. There
 * `globalThis.navigator.locks` is `undefined`, so `navigatorLock`'s
 * `navigator.locks.request(...)` call rejects with a TypeError
 * (`undefined is not an object`) — auth-js re-throws that out of the
 * auto-refresh timer exactly like BAL-792 (BALLEE-GP).
 *
 * Deliberately NOT mapped onto `NavigatorLockAcquireTimeoutError` the way the
 * lifecycle teardown is: a missing API is PERMANENT, not transient. Reporting
 * "lock busy" on every tick would make `_autoRefreshTokenTick` skip forever and
 * the session would silently expire — strictly worse than the unhandled
 * rejection. Instead we don't arm the lock at all, which drops auth-js to its
 * lockless default path (`settings.lock == null`, `GoTrueClient.js:199`). That
 * path never re-throws from the tick. auth-js endorses exactly this: "If the
 * API is not available, this function will throw. Make sure you check
 * availability before configuring GoTrueClient" (`lib/locks.js:54-56`).
 *
 * We ACCEPT losing BAL-752's cross-tab refresh mutex on this browser class —
 * it is not "no loss". `navigator.locks` is also absent in any insecure
 * context (plain `http://`, non-localhost), where multi-tab browsing IS
 * possible and the `/token` stampede IS therefore reachable. Production is
 * https, so real exposure is near zero, and a BroadcastChannel/storage mutex
 * is buildable but not worth building for it. Recorded so the trade-off is
 * visible rather than implied.
 *
 * Residual gap (watch, not fixed): an OPAQUE origin (sandboxed iframe without
 * `allow-same-origin`) has `locks.request` as a function, so this detect
 * passes, but `request()` then rejects with a `SecurityError` — permanently,
 * down this same escape route. No synchronous detect can catch it (the
 * rejection happens at call time, not property access), and degrading inside
 * the wrapper is unsafe because `navigatorLock` invokes `fn()` INSIDE the
 * lock callback, making a callback error indistinguishable from a lock
 * failure at the wrapper boundary — running `fn()` anyway risks a double
 * refresh. Closing it needs an async probe, which does not fit this sync
 * factory. No such Sentry issue exists over 90 days; revisit if one appears.
 */
function supportsWebLocks(): boolean {
  return typeof globalThis.navigator?.locks?.request === 'function';
}

/**
 * @name getSupabaseBrowserClient
 * @description Get a Supabase client for use in the Browser
 */
export function getSupabaseBrowserClient<GenericSchema = Database>() {
  // On the server there are no tabs and no auto-refresh timer to dedupe, and
  // `navigatorLock` throws when `navigator.locks` is absent — so never cache
  // and never attach the cross-tab lock. Always hand back a fresh client.
  if (typeof window === 'undefined') {
    const keys = getSupabaseClientKeys();

    return createBrowserClient<Database>(keys.url, keys.publicKey, {
      global: reportingFetchOptions(),
    }) as unknown as ReturnType<typeof createBrowserClient<GenericSchema>>;
  }

  browserClient ??= instantiateBrowserClient();

  return browserClient as unknown as ReturnType<
    typeof createBrowserClient<GenericSchema>
  >;
}

function instantiateBrowserClient() {
  const keys = getSupabaseClientKeys();

  // No Web Locks API (BALLEE-GP) — fall back to auth-js's lockless
  // coordination instead of arming a lock that can only ever reject.
  if (!supportsWebLocks()) {
    return createBrowserClient<Database>(keys.url, keys.publicKey, {
      global: reportingFetchOptions(),
    });
  }

  return createBrowserClient<Database>(keys.url, keys.publicKey, {
    global: reportingFetchOptions(),
    auth: {
      /**
       * Cross-tab refresh mutex via the Web Locks API.
       *
       * `@supabase/auth-js@2.110` defaults `lock` to `null` (lockless
       * coordination): its single-flight only dedupes refreshes WITHIN one
       * GoTrueClient instance, so two tabs — each with their own instance —
       * both tick, both read the not-yet-rotated token from storage, and both
       * hit `/token` in the same second. Passing a lock re-arms the
       * origin-global serialization: `_autoRefreshTokenTick` acquires
       * `lock:<storageKey>` with `acquireTimeout: 0`, so only the tab holding
       * the lock refreshes and every other tab's tick fails fast and skips.
       * The storageKey is derived from the (url, key) pair, identical across
       * tabs, so the lock name is shared.
       *
       * (auth-js marks `navigatorLock` `@deprecated` with a "has no effect"
       * note aimed at a future v3 that removes the lock path — but in 2.110
       * `_autoRefreshTokenTick` and `_callRefreshToken` still honor a custom
       * lock. Verified against the resolved source.)
       *
       * Wrapped by `lifecycleSafeNavigatorLock` (BAL-792): a Chromium
       * page-lifecycle teardown makes `navigator.locks.request` reject with
       * "The provided callback is no longer runnable.", which auth-js re-throws
       * out of the auto-refresh timer as an unhandled rejection. The wrapper
       * routes that one failure into auth-js's existing timeout-skip branch.
       */
      lock: lifecycleSafeNavigatorLock,
    },
  });
}
